import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, TrendingUp, TrendingDown, Check, Activity } from "lucide-react";
import { fmtNum } from "../lib/api";

const RANK_COLORS = ["#00ff66", "#00f0ff", "#ff007a", "#ffd028"];

export const CoinCard = ({ coin, rank, hasVoted, votedThis, onVote }) => {
  const [bursts, setBursts] = useState([]);
  const accent = RANK_COLORS[rank % 4];
  const change = coin.change24h;
  const up = change === null || change === undefined ? null : change >= 0;

  const handleVote = () => {
    if (hasVoted) return;
    const id = Date.now();
    setBursts((b) => [...b, id]);
    setTimeout(() => setBursts((b) => b.filter((x) => x !== id)), 1000);
    onVote(coin);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -30, scale: 0.94 }}
      transition={{ type: "spring", stiffness: 240, damping: 26 }}
      data-testid={`coin-card-${coin.symbol}`}
      className="relative rounded-2xl border border-white/10 bg-[#0c0f17]/80 backdrop-blur-xl p-5 flex flex-col overflow-hidden group"
      style={{ boxShadow: votedThis ? `0 0 30px ${accent}44` : undefined }}
    >
      <div
        className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-25 transition-opacity group-hover:opacity-45"
        style={{ background: accent }}
      />

      <div className="flex items-center justify-between relative z-10">
        <span
          className="font-mono-num text-xs font-black px-2 py-1 rounded-md border"
          style={{ color: accent, borderColor: `${accent}55`, background: `${accent}12` }}
        >
          #{rank + 1}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-sm font-mono-num font-bold ${
            up === null ? "text-slate-400" : up ? "text-emerald-400" : "text-rose-400"
          }`}
        >
          {up === null ? (
            "N/A"
          ) : (
            <>
              {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {up ? "+" : ""}
              {change.toFixed(1)}%
            </>
          )}
        </span>
      </div>

      <div className="flex items-center gap-3 mt-4 relative z-10">
        <div className="relative shrink-0">
          <div
            className="absolute inset-0 rounded-full blur-md opacity-60"
            style={{ background: accent }}
          />
          <img
            src={coin.image}
            alt={coin.name}
            onError={(e) => {
              e.currentTarget.src = `https://placehold.co/96x96/0c0f17/${accent.slice(
                1
              )}?text=${coin.symbol.slice(0, 3)}`;
            }}
            className="relative h-14 w-14 rounded-full object-cover border-2 border-white/20"
          />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-base truncate leading-tight" title={coin.name}>
            {coin.name}
          </div>
          <div className="font-mono-num text-sm font-bold tracking-wider" style={{ color: accent }}>
            ${coin.symbol}
          </div>
        </div>
      </div>

      {/* votes + share */}
      <div className="mt-5 relative z-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Votes
            </div>
            <div
              data-testid={`coin-votes-${coin.symbol}`}
              className="font-mono-num font-black text-2xl leading-none"
            >
              {coin.votes.toLocaleString()}
            </div>
          </div>
          <div className="font-mono-num font-black text-2xl" style={{ color: accent }}>
            {coin.percent}%
          </div>
        </div>
        <div className="mt-2 h-2.5 w-full rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: accent }}
            animate={{ width: `${coin.percent}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs text-slate-400 font-mono-num relative z-10">
        <span className="inline-flex items-center gap-1">
          <Activity size={12} className="text-slate-500" /> Vol ${fmtNum(coin.volume24h)}
        </span>
        <span className="text-slate-600">·</span>
        <span>{fmtNum(coin.buys24h)} buys / {fmtNum(coin.sells24h)} sells</span>
      </div>

      {/* vote button */}
      <button
        data-testid={`vote-button-${coin.symbol}`}
        onClick={handleVote}
        disabled={hasVoted}
        className={`mt-5 relative z-10 w-full py-3.5 rounded-xl font-black uppercase text-sm tracking-wider flex items-center justify-center gap-2 transition-[transform,background-color,box-shadow] duration-200 ${
          votedThis
            ? "bg-emerald-400 text-black cursor-default"
            : hasVoted
            ? "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed"
            : "text-black active:scale-95 hover:shadow-[0_0_22px_rgba(0,255,102,0.5)]"
        }`}
        style={
          !hasVoted
            ? { background: `linear-gradient(90deg, ${accent}, #00f0ff)` }
            : undefined
        }
      >
        {votedThis ? (
          <>
            <Check size={16} /> Sent it!
          </>
        ) : hasVoted ? (
          "Voted this round"
        ) : (
          <>
            <Rocket size={16} /> SEND IT
          </>
        )}

        <AnimatePresence>
          {bursts.map((id) => (
            <span
              key={id}
              className="float-up absolute -top-2 font-display font-black text-lg pointer-events-none"
              style={{ color: accent }}
            >
              +1 🚀
            </span>
          ))}
        </AnimatePresence>
      </button>
    </motion.div>
  );
};
